import { Card, CardTitle, Notice, Skeleton, formatAmount, formatFiat } from "@custos/ui";
import { ArrowDownToLine, ArrowLeft, ArrowLeftRight, ArrowUpFromLine } from "lucide-react";
import { useMemo } from "react";
import { Link, useParams } from "react-router";
import { PageHeader } from "../components/AppShell";
import { AssetIcon, Row } from "../components/bits";
import { filterActivity, mergeActivity } from "../lib/activity";
import { findAsset, findChain, networkLabel } from "../lib/chains";
import { useMask, usePrefs } from "../lib/prefs";
import { useBalances, useConfig, useDeposits, usePortfolio, useSwaps, useWithdrawals } from "../lib/queries";
import { ActivityList } from "./Activity";

export function AssetDetailPage() {
  const { chain = "", asset = "" } = useParams();
  const config = useConfig();
  const balances = useBalances();
  const deposits = useDeposits();
  const withdrawals = useWithdrawals();
  const swaps = useSwaps();
  const currency = usePrefs((st) => st.currency);
  const mask = useMask();
  const portfolio = usePortfolio(currency);
  const priced = portfolio.data?.assets.find((a) => a.chain === chain && a.asset === asset);

  const info = findAsset(config.data?.assets ?? [], chain, asset);
  const chainInfo = findChain(config.data?.chains ?? [], chain);
  const balance = balances.data?.find((b) => b.chain === chain && b.asset === asset);
  const items = useMemo(
    () =>
      filterActivity(mergeActivity(deposits.data ?? [], withdrawals.data ?? [], swaps.data ?? []), {
        type: "all",
        asset: `${chain}:${asset}`,
        from: "",
        to: "",
      }),
    [deposits.data, withdrawals.data, swaps.data, chain, asset],
  );

  if (config.data && !info) {
    return (
      <>
        <PageHeader title="Asset not found" />
        <Notice tone="warning">This asset isn't available on this network.</Notice>
      </>
    );
  }

  return (
    <>
      <Link to="/" className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-fg">
        <ArrowLeft className="size-4" aria-hidden /> Home
      </Link>
      <PageHeader title={`${asset} · ${info?.name ?? ""}`} subtitle={networkLabel(chainInfo, chain)} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className="bg-aurora animate-rise rounded-[32px] p-5 sm:p-8 lg:col-span-2">
          <div className="flex items-center gap-3">
            <AssetIcon symbol={asset} className="size-14 text-2xl" />
            <div className="min-w-0">
              <p className="text-sm text-white/80">Account balance</p>
              {balances.isPending ? (
                <Skeleton className="mt-1 h-8 w-40" />
              ) : (
                <p
                  className="text-4xl tracking-[-0.03em] text-white tabular-nums sm:text-5xl"
                  data-testid="asset-balance"
                >
                  {mask(formatAmount(balance?.balance ?? "0", 8))}{" "}
                  <span className="text-lg text-white/70">{asset}</span>
                </p>
              )}
              <p className="mt-1 font-mono text-sm text-white/85 tabular-nums" data-testid="asset-fiat-value">
                {priced?.value != null ? mask(formatFiat(priced.value, currency)) : "—"}
                {priced?.price != null && (
                  <span className="text-white/65">
                    {" "}
                    · 1 {asset} = {formatFiat(priced.price, currency)}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2 sm:max-w-md">
            <Link
              to={`/deposit?chain=${chain}`}
              className="inline-flex min-h-13 items-center justify-center gap-2 rounded-full bg-white font-medium text-[#0B0A10] active:scale-[0.97]"
            >
              <ArrowDownToLine className="size-4" aria-hidden /> Deposit
            </Link>
            <Link
              to={`/withdraw?chain=${chain}&asset=${asset}`}
              className="glass-circle inline-flex min-h-13 items-center justify-center gap-2 rounded-full font-medium active:scale-[0.97]"
            >
              <ArrowUpFromLine className="size-4" aria-hidden /> Withdraw
            </Link>
            <Link
              to={`/swap?chain=${chain}&from=${asset}`}
              className="glass-circle inline-flex min-h-13 items-center justify-center gap-2 rounded-full font-medium active:scale-[0.97]"
            >
              <ArrowLeftRight className="size-4" aria-hidden /> Swap
            </Link>
          </div>
        </section>
        {info && (
          <Card>
            <CardTitle className="mb-2">Details</CardTitle>
            <Row label="Network">{networkLabel(chainInfo, chain)}</Row>
            <Row label="Minimum deposit">
              {info.minDeposit} {asset}
            </Row>
            <Row label="Minimum withdrawal">
              {info.minWithdrawal} {asset}
            </Row>
            <Row label="Withdrawal fee">
              {info.withdrawalFee} {asset}
            </Row>
            <Row label="Confirmations">{info.minConfirmations}</Row>
          </Card>
        )}
      </div>
      <section className="mt-6">
        <h2 className="mb-3 font-heading text-lg font-semibold text-fg">Recent activity</h2>
        <ActivityList
          items={items.slice(0, 10)}
          loading={deposits.isPending || withdrawals.isPending || swaps.isPending}
        />
      </section>
    </>
  );
}
